import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  Alert,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HistoryIcon from '@mui/icons-material/History';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AppBreadcrumbs from '../components/common/AppBreadcrumbs';
import FormSkeleton from '../components/common/FormSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HistorieTimeline from '../components/common/HistorieTimeline';
import InvoiceStatusPipeline from '../components/common/InvoiceStatusPipeline';
import { useNotification } from '../hooks/NotificationContext';
import { toLocalDateString, calendarMonthRange, previousCalendarMonthRange } from '../utils/time';
import { isKontingentProjektTyp, projektTypLabel } from '../utils/projektTyp';
import rechnungenService from '../services/rechnungenService';
import { projekteService } from '../services/projekteService';
import { kundenService } from '../services/kundenService';
import angeboteService from '../services/angeboteService';
import zeiteintraegeService from '../services/zeiteintraegeService';
import {
  Rechnung, RechnungCreate, RechnungspositionCreate, ProjektList, RechnungsaufteilungsAnteil,
  Kunde, RechnungTextvorlage, Steuerfall, Zeiteintrag,
} from '../types';
import { STEUERFALL_OPTIONEN, steuersatzFuer, steuerfallHinweis, vorschlageSteuerfall } from '../utils/steuerfall';

/* ── Accordion styling (matching AuditDetailPage) ── */
const accordionSx = (isDark: boolean) => ({
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
  borderRadius: '12px !important',
  overflow: 'hidden',
  mb: 2,
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mb: 2 },
  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
});

const accordionSummarySx = (isDark: boolean) => ({
  background: isDark
    ? 'linear-gradient(135deg, rgba(30,36,68,0.7) 0%, rgba(22,28,52,0.5) 100%)'
    : 'linear-gradient(135deg, rgba(245,247,252,1) 0%, rgba(238,241,248,1) 100%)',
  minHeight: 56,
  '&.Mui-expanded': { minHeight: 56 },
  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
});

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ fontSize: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled', display: 'block', mb: 0.25 }}
  >
    {children}
  </Typography>
);

const RechnungDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isNew = id === 'neu';

  const [rechnung, setRechnung] = useState<Rechnung | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [zugferdLoading, setZugferdLoading] = useState(false);
  const [splitAnteile, setSplitAnteile] = useState<RechnungsaufteilungsAnteil[]>([]);
  const [splitting, setSplitting] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generatePreview, setGeneratePreview] = useState<{ anzahl: number; stunden: number } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [linkedEntries, setLinkedEntries] = useState<Zeiteintrag[]>([]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { showSuccess, showError } = useNotification();

  const [expandedPanels, setExpandedPanels] = useState({
    positionen: true,
    zeiteintraege: true,
    referenzen: false,
    notizen: false,
  });

  const [projektMenuAnchor, setProjektMenuAnchor] = useState<null | HTMLElement>(null);

  const [projekte, setProjekte] = useState<ProjektList[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [textvorlagen, setTextvorlagen] = useState<RechnungTextvorlage[]>([]);
  const [loadingProjekte, setLoadingProjekte] = useState(true);

  const [formData, setFormData] = useState<RechnungCreate>(() => {
    const vorherigerMonat = previousCalendarMonthRange();
    return {
    rechnungsnummer: '',
    rechnungsdatum: toLocalDateString(),
    leistungszeitraum_von: vorherigerMonat.von,
    leistungszeitraum_bis: vorherigerMonat.bis,
    status: 'Entwurf',
    zahlungsziel_tage: 30,
    zahlungsbedingungen: 'Zahlbar innerhalb von 30 Tagen nach Rechnungsdatum.',
    nettobetrag: 0,
    steuersatz: 19.00,
    steuerfall: 'inland',
    steuerbetrag: 0,
    bruttobetrag: 0,
    notizen: '',
    projekt_id: parseInt(searchParams.get('projekt_id') || '0'),
    positionen: [],
    rechnungsempfaenger_kunde_id: null,
    textvorlage_id: null,
    leitweg_id: '',
    bestellnummer: '',
    vertragsnummer: '',
    vergabenummer: '',
    angebotsnummer: '',
    angebot_id: null,
  };
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProjekte(true);
        const [projektListe, kundenListe, vorlagenListe] = await Promise.all([
          projekteService.list(),
          kundenService.list(),
          rechnungenService.listTextvorlagen(),
        ]);
        setProjekte(projektListe);
        setKunden(kundenListe);
        setTextvorlagen(vorlagenListe);
      } catch { /* interceptor */ } finally {
        setLoadingProjekte(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (isNew) {
      rechnungenService.getNextNumber().then((num) => {
        setFormData((prev) => ({ ...prev, rechnungsnummer: num }));
      });
    }
  }, [isNew]);

  useEffect(() => {
    if (!isNew || !formData.projekt_id || projekte.length === 0) return;
    const projekt = projekte.find((p) => p.id === formData.projekt_id);
    const emp = formData.rechnungsempfaenger_kunde_id
      ? kunden.find((k) => k.id === formData.rechnungsempfaenger_kunde_id)
      : projekt?.kunde;
    if (!emp) return;
    const fall = vorschlageSteuerfall(emp.land, emp.ust_id);
    if (fall === 'inland') return;
    setFormData((prev) => {
      if (prev.steuerfall && prev.steuerfall !== 'inland') return prev;
      const satz = steuersatzFuer(fall);
      return { ...prev, steuerfall: fall, steuersatz: satz, ...recalculate(prev.positionen || [], satz) };
    });
  }, [isNew, projekte, kunden, formData.projekt_id, formData.rechnungsempfaenger_kunde_id]);

  useEffect(() => {
    if (!isNew || !formData.projekt_id) return;
    let cancelled = false;
    angeboteService.list({ projekt_id: formData.projekt_id }).then((list) => {
      if (cancelled || list.length === 0) return;
      const src = list[0];
      setFormData((prev) => {
        if (prev.projekt_id !== src.projekt_id) return prev;
        if (prev.angebotsnummer || prev.angebot_id) return prev;
        return { ...prev, angebotsnummer: src.angebotsnummer, angebot_id: src.id };
      });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [isNew, formData.projekt_id]);

  const loadRechnung = useCallback(async () => {
    if (isNew || !id) return;
    try {
      setLoading(true);
      const data = await rechnungenService.get(parseInt(id));
      setRechnung(data);
      setSplitAnteile(data.aufteilung_defaults || []);
      setFormData({
        rechnungsnummer: data.rechnungsnummer,
        rechnungsdatum: data.rechnungsdatum,
        leistungszeitraum_von: data.leistungszeitraum_von,
        leistungszeitraum_bis: data.leistungszeitraum_bis,
        status: data.status,
        zahlungsziel_tage: data.zahlungsziel_tage,
        zahlungsbedingungen: data.zahlungsbedingungen || '',
        nettobetrag: data.nettobetrag,
        steuersatz: data.steuersatz,
        steuerfall: data.steuerfall || 'inland',
        steuerbetrag: data.steuerbetrag,
        bruttobetrag: data.bruttobetrag,
        notizen: data.notizen || '',
        projekt_id: data.projekt_id,
        rechnungsempfaenger_kunde_id: data.rechnungsempfaenger_kunde_id ?? null,
        textvorlage_id: data.textvorlage_id ?? null,
        leitweg_id: data.leitweg_id || '',
        bestellnummer: data.bestellnummer || '',
        vertragsnummer: data.vertragsnummer || '',
        vergabenummer: data.vergabenummer || '',
        angebotsnummer: data.angebotsnummer || '',
        angebot_id: data.angebot_id ?? null,
        positionen: data.positionen.map((p) => ({
          beschreibung: p.beschreibung,
          menge: p.menge,
          einheit: p.einheit,
          einzelpreis: p.einzelpreis,
          gesamtpreis: p.gesamtpreis,
          sort_order: p.sort_order,
        })),
      });
      const verknuepft = await zeiteintraegeService.list({ rechnung_id: data.id });
      setLinkedEntries(verknuepft);
    } catch { /* interceptor */ } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadRechnung(); }, [loadRechnung]);

  const isEditable = !rechnung || rechnung.status === 'Entwurf';

  const recalculate = (positionen: RechnungspositionCreate[], steuersatz: number) => {
    const netto = positionen.reduce((s, p) => s + p.gesamtpreis, 0);
    const steuer = Math.round(netto * steuersatz / 100 * 100) / 100;
    return {
      nettobetrag: Math.round(netto * 100) / 100,
      steuerbetrag: steuer,
      bruttobetrag: Math.round((netto + steuer) * 100) / 100,
    };
  };

  const applySteuerfall = (updated: RechnungCreate, fall: Steuerfall): RechnungCreate => {
    const satz = steuersatzFuer(fall);
    return {
      ...updated,
      steuerfall: fall,
      steuersatz: satz,
      ...recalculate(updated.positionen || [], satz),
    };
  };

  const handleInputChange = (field: keyof RechnungCreate, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'steuerfall') {
        return applySteuerfall(updated, value as Steuerfall);
      }
      if (field === 'steuersatz') {
        return { ...updated, ...recalculate(updated.positionen || [], value) };
      }
      if (field === 'projekt_id') {
        const projekt = projekte.find((p) => p.id === value);
        const kunde = projekt?.kunde;
        updated.angebotsnummer = '';
        updated.angebot_id = null;
        if (kunde?.rechnung_textvorlage_id && !prev.textvorlage_id) {
          updated.textvorlage_id = kunde.rechnung_textvorlage_id;
        }
        if (!prev.leitweg_id && !prev.rechnungsempfaenger_kunde_id && kunde?.leitweg_id) {
          updated.leitweg_id = kunde.leitweg_id;
        }
        if (!prev.rechnungsempfaenger_kunde_id && kunde) {
          return applySteuerfall(updated, vorschlageSteuerfall(kunde.land, kunde.ust_id));
        }
      }
      if (field === 'rechnungsempfaenger_kunde_id') {
        const empfaengerKunde = value
          ? kunden.find((k) => k.id === value)
          : projekte.find((p) => p.id === prev.projekt_id)?.kunde;
        if (empfaengerKunde?.rechnung_textvorlage_id) {
          updated.textvorlage_id = empfaengerKunde.rechnung_textvorlage_id;
        }
        if (!prev.leitweg_id && empfaengerKunde?.leitweg_id) {
          updated.leitweg_id = empfaengerKunde.leitweg_id;
        }
        if (empfaengerKunde) {
          return applySteuerfall(updated, vorschlageSteuerfall(empfaengerKunde.land, empfaengerKunde.ust_id));
        }
      }
      return updated;
    });
  };

  const handleAddPosition = () => {
    const pos = formData.positionen || [];
    const sel = projekte.find((p) => p.id === formData.projekt_id);
    const tagessatz = sel?.tagessatz || 0;
    setFormData((prev) => ({
      ...prev,
      positionen: [...pos, { beschreibung: '', menge: 1, einheit: 'PT', einzelpreis: tagessatz, gesamtpreis: tagessatz, sort_order: pos.length + 1 }],
    }));
  };

  const handleUpdatePosition = (index: number, field: keyof RechnungspositionCreate, value: any) => {
    const pos = [...(formData.positionen || [])];
    (pos[index] as any)[field] = value;
    if (field === 'menge' || field === 'einzelpreis') {
      pos[index].gesamtpreis = Math.round(pos[index].menge * pos[index].einzelpreis * 100) / 100;
    }
    const totals = recalculate(pos, formData.steuersatz || 19);
    setFormData((prev) => ({ ...prev, positionen: pos, ...totals }));
  };

  const handleRemovePosition = (index: number) => {
    const pos = (formData.positionen || []).filter((_, i) => i !== index);
    const totals = recalculate(pos, formData.steuersatz || 19);
    setFormData((prev) => ({ ...prev, positionen: pos, ...totals }));
  };

  const handleSave = async () => {
    if (isNew && (!formData.projekt_id || formData.projekt_id <= 0)) {
      showError('Bitte ein Projekt auswählen.');
      return;
    }
    if (isNew && (!formData.leistungszeitraum_von || !formData.leistungszeitraum_bis)) {
      showError('Leistungszeitraum (von/bis) muss ausgefüllt sein.');
      return;
    }
    try {
      setSaving(true);
      if (isNew) {
        const created = await rechnungenService.create(formData);
        showSuccess('Rechnung erfolgreich erstellt');
        setTimeout(() => navigate(`/rechnungen/${created.id}`), 1000);
      } else if (id) {
        await rechnungenService.update(parseInt(id), formData);
        showSuccess('Rechnung erfolgreich aktualisiert');
        loadRechnung();
        setEditing(false);
      }
    } catch { /* interceptor */ } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id || isNew) return;
    try {
      await rechnungenService.updateStatus(parseInt(id), newStatus);
      showSuccess(`Status auf "${newStatus}" geändert`);
      loadRechnung();
    } catch { /* interceptor */ }
  };

  const handleGenerateFromZeiteintraege = async () => {
    if (!id || isNew || !formData.leistungszeitraum_von || !formData.leistungszeitraum_bis) {
      showError('Bitte zuerst den Leistungszeitraum setzen.');
      return;
    }
    try {
      const entries = await zeiteintraegeService.list({
        projekt_id: formData.projekt_id,
        datum_von: formData.leistungszeitraum_von,
        datum_bis: formData.leistungszeitraum_bis,
        abgerechnet: false,
      });
      if (entries.length === 0) {
        showError('Keine nicht-abgerechneten Zeiteinträge im Leistungszeitraum.');
        return;
      }
      setGeneratePreview({
        anzahl: entries.length,
        stunden: entries.reduce((s, e) => s + e.stunden, 0),
      });
      setGenerateDialogOpen(true);
    } catch { /* interceptor */ }
  };

  const handleConfirmGenerate = async () => {
    if (!id || isNew) return;
    try {
      setGenerating(true);
      await rechnungenService.generateFromZeiteintraege(parseInt(id), {
        leistungszeitraum_von: formData.leistungszeitraum_von,
        leistungszeitraum_bis: formData.leistungszeitraum_bis,
      });
      showSuccess('Positionen aus Zeiteinträgen im Leistungszeitraum generiert');
      setGenerateDialogOpen(false);
      await loadRechnung();
    } catch { /* interceptor */ } finally {
      setGenerating(false);
    }
  };

  const handleSyncZeiteintraege = async () => {
    if (!id || isNew) return;
    try {
      setSyncing(true);
      const result = await rechnungenService.syncZeiteintraege(parseInt(id));
      showSuccess(
        result.anzahl_geloest === 0
          ? 'Keine Einträge außerhalb des Leistungszeitraums'
          : `${result.anzahl_geloest} Zeiteinträge (${result.stunden_geloest.toFixed(1)}h) wieder auf nicht abgerechnet gesetzt`,
      );
      setSyncDialogOpen(false);
      await loadRechnung();
    } catch { /* interceptor */ } finally {
      setSyncing(false);
    }
  };

  const applyLeistungszeitraum = (range: { von: string; bis: string }) => {
    setFormData((prev) => ({
      ...prev,
      leistungszeitraum_von: range.von,
      leistungszeitraum_bis: range.bis,
    }));
  };

  const ausserhalbZeitraum = linkedEntries.filter((ze) => {
    if (!formData.leistungszeitraum_von || !formData.leistungszeitraum_bis) return false;
    return ze.datum < formData.leistungszeitraum_von || ze.datum > formData.leistungszeitraum_bis;
  });

  const handleZugferdDownload = async () => {
    if (!id || isNew) return;
    setZugferdLoading(true);
    try {
      await rechnungenService.generateZugferdPdf(parseInt(id));
      showSuccess('E-Rechnung (ZUGFeRD) erstellt');
    } catch (error) {
      // Der Interceptor kennt die Blob-Fehlermeldung nicht, daher hier anzeigen.
      showError(error instanceof Error ? error.message : 'E-Rechnung konnte nicht erstellt werden');
    } finally {
      setZugferdLoading(false);
    }
  };

  const handleSplitProzent = (index: number, value: number) => {
    const pct = Math.max(0, Math.min(100, value));
    setSplitAnteile((prev) => {
      const next = prev.map((a) => ({ ...a }));
      if (!next[index] || !next[1 - index]) return prev;
      next[index].prozent = pct;
      next[1 - index].prozent = Math.round((100 - pct) * 100) / 100;
      return next;
    });
  };

  const handleAufteilen = async () => {
    if (!id || isNew) return;
    const summe = splitAnteile.reduce((s, a) => s + Number(a.prozent), 0);
    if (Math.abs(summe - 100) > 0.01) {
      showError('Die Prozentanteile müssen in Summe 100 ergeben.');
      return;
    }
    try {
      setSplitting(true);
      const result = await rechnungenService.aufteilen(parseInt(id), splitAnteile);
      showSuccess('Rechnung in zwei Empfänger-Rechnungen aufgeteilt');
      setSplitDialogOpen(false);
      const first = result.rechnungen[0];
      if (first?.id) {
        navigate(`/rechnungen/${first.id}`);
      }
      loadRechnung();
    } catch { /* interceptor */ } finally {
      setSplitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      await rechnungenService.delete(parseInt(id));
      showSuccess('Rechnung gelöscht');
      setDeleteDialogOpen(false);
      setTimeout(() => navigate('/rechnungen'), 1000);
    } catch {
      setDeleteDialogOpen(false);
    }
  };

  const handleStartEditing = () => setEditing(true);
  const handleCancelEditing = () => {
    if (rechnung) {
      setFormData({
        rechnungsnummer: rechnung.rechnungsnummer,
        rechnungsdatum: rechnung.rechnungsdatum,
        leistungszeitraum_von: rechnung.leistungszeitraum_von,
        leistungszeitraum_bis: rechnung.leistungszeitraum_bis,
        status: rechnung.status,
        zahlungsziel_tage: rechnung.zahlungsziel_tage,
        zahlungsbedingungen: rechnung.zahlungsbedingungen || '',
        nettobetrag: rechnung.nettobetrag,
        steuersatz: rechnung.steuersatz,
        steuerfall: rechnung.steuerfall || 'inland',
        steuerbetrag: rechnung.steuerbetrag,
        bruttobetrag: rechnung.bruttobetrag,
        notizen: rechnung.notizen || '',
        projekt_id: rechnung.projekt_id,
        rechnungsempfaenger_kunde_id: rechnung.rechnungsempfaenger_kunde_id ?? null,
        textvorlage_id: rechnung.textvorlage_id ?? null,
        leitweg_id: rechnung.leitweg_id || '',
        bestellnummer: rechnung.bestellnummer || '',
        vertragsnummer: rechnung.vertragsnummer || '',
        vergabenummer: rechnung.vergabenummer || '',
        angebotsnummer: rechnung.angebotsnummer || '',
        angebot_id: rechnung.angebot_id ?? null,
        positionen: rechnung.positionen.map((p) => ({
          beschreibung: p.beschreibung, menge: p.menge, einheit: p.einheit,
          einzelpreis: p.einzelpreis, gesamtpreis: p.gesamtpreis, sort_order: p.sort_order,
        })),
      });
    }
    setEditing(false);
  };

  const handleAccordionChange = (panel: keyof typeof expandedPanels) =>
    (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) => ({ ...prev, [panel]: isExpanded }));
    };

  const selectedProjekt = projekte.find((p) => p.id === formData.projekt_id);
  const isKontingentProjekt = isKontingentProjektTyp(selectedProjekt?.projekt_typ);
  const projektKunde = selectedProjekt?.kunde;
  const empfaengerId = formData.rechnungsempfaenger_kunde_id || projektKunde?.id || 0;
  const empfaenger = kunden.find((k) => k.id === empfaengerId) || projektKunde;
  const kundeName = rechnung?.kunde_name || projektKunde?.name_des_unternehmens;
  const empfaengerName = rechnung?.empfaenger_name || empfaenger?.name_des_unternehmens;
  const kundenLeitweg = empfaenger?.leitweg_id || '';

  const referenzFelder = (editable: boolean) => (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Leitweg-ID"
          value={formData.leitweg_id || ''}
          onChange={(e) => handleInputChange('leitweg_id', e.target.value)}
          disabled={!editable}
          helperText={
            !formData.leitweg_id && kundenLeitweg
              ? `Standard vom Empfänger: ${kundenLeitweg}`
              : 'Überschreibt die Leitweg-ID des Empfängers, falls abweichend'
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Bestellnummer"
          value={formData.bestellnummer || ''}
          onChange={(e) => handleInputChange('bestellnummer', e.target.value)}
          disabled={!editable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Vertragsnummer"
          value={formData.vertragsnummer || ''}
          onChange={(e) => handleInputChange('vertragsnummer', e.target.value)}
          disabled={!editable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Vergabenummer"
          value={formData.vergabenummer || ''}
          onChange={(e) => handleInputChange('vergabenummer', e.target.value)}
          disabled={!editable}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Angebotsnummer"
          value={formData.angebotsnummer || ''}
          onChange={(e) => handleInputChange('angebotsnummer', e.target.value)}
          disabled={!editable}
        />
      </Grid>
    </>
  );

  const formatDate = (d: string) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  if (loading || loadingProjekte) {
    return <FormSkeleton fields={8} />;
  }

  /* ══ New invoice form ══ */
  if (isNew) {
    return (
      <Box>
        <AppBreadcrumbs items={[{ label: 'Rechnungen', path: '/rechnungen' }, { label: 'Neue Rechnung' }]} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, mt: 1 }}>Neue Rechnung</Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Rechnungsdaten</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Rechnungsnummer" value={formData.rechnungsnummer}
                  onChange={(e) => handleInputChange('rechnungsnummer', e.target.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Projekt</InputLabel>
                  <Select value={formData.projekt_id || ''} onChange={(e) => handleInputChange('projekt_id', e.target.value as number)} label="Projekt">
                    {projekte.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} ({projektTypLabel(p.projekt_typ)})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Rechnungsempfänger</InputLabel>
                  <Select
                    value={formData.rechnungsempfaenger_kunde_id ?? ''}
                    onChange={(e) => handleInputChange('rechnungsempfaenger_kunde_id', e.target.value === '' ? null : Number(e.target.value))}
                    label="Rechnungsempfänger"
                  >
                    <MenuItem value="">Projektkunde{projektKunde ? ` (${projektKunde.name_des_unternehmens})` : ''}</MenuItem>
                    {kunden.map((k) => (
                      <MenuItem key={k.id} value={k.id}>{k.name_des_unternehmens}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {empfaenger && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {[empfaenger.strasse, `${empfaenger.plz || ''} ${empfaenger.ort || ''}`.trim(), empfaenger.land, empfaenger.ust_id].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Textvorlage</InputLabel>
                  <Select
                    value={formData.textvorlage_id ?? ''}
                    onChange={(e) => handleInputChange('textvorlage_id', e.target.value === '' ? null : Number(e.target.value))}
                    label="Textvorlage"
                  >
                    <MenuItem value="">Standard / Kundenzuordnung</MenuItem>
                    {textvorlagen.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name}{v.is_default ? ' (Standard)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Rechnungsdatum" type="date" value={formData.rechnungsdatum}
                  onChange={(e) => handleInputChange('rechnungsdatum', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Leistungszeitraum von" type="date" value={formData.leistungszeitraum_von}
                  onChange={(e) => handleInputChange('leistungszeitraum_von', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Leistungszeitraum bis" type="date" value={formData.leistungszeitraum_bis}
                  onChange={(e) => handleInputChange('leistungszeitraum_bis', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => applyLeistungszeitraum(previousCalendarMonthRange())}>
                    Vorheriger Monat
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => applyLeistungszeitraum(calendarMonthRange())}>
                    Aktueller Monat
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Zahlungsziel (Tage)" type="number" value={formData.zahlungsziel_tage}
                  onChange={(e) => handleInputChange('zahlungsziel_tage', parseInt(e.target.value) || 30)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Steuerfall</InputLabel>
                  <Select
                    label="Steuerfall"
                    value={formData.steuerfall || 'inland'}
                    onChange={(e) => handleInputChange('steuerfall', e.target.value)}
                  >
                    {STEUERFALL_OPTIONEN.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Steuersatz (%)" type="number" value={formData.steuersatz}
                  InputProps={{ readOnly: true }} />
              </Grid>
              {steuerfallHinweis(formData.steuerfall) && (
                <Grid item xs={12}>
                  <Alert severity="warning">{steuerfallHinweis(formData.steuerfall)}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Auftragsreferenzen
                </Typography>
              </Grid>
              {referenzFelder(true)}
              <Grid item xs={12}>
                <TextField fullWidth label="Zahlungsbedingungen" value={formData.zahlungsbedingungen}
                  onChange={(e) => handleInputChange('zahlungsbedingungen', e.target.value)} multiline rows={2} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notizen (intern)" value={formData.notizen}
                  onChange={(e) => handleInputChange('notizen', e.target.value)} multiline rows={2} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Positions for new invoice */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Positionen</Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddPosition}>
                Position hinzufügen
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>Pos.</TableCell>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell width={100}>Menge</TableCell>
                    <TableCell width={100}>Einheit</TableCell>
                    <TableCell width={130}>Einzelpreis</TableCell>
                    <TableCell width={130} align="right">Gesamt</TableCell>
                    <TableCell width={50} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(formData.positionen || []).map((pos, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <TextField fullWidth size="small" variant="standard" value={pos.beschreibung}
                          onChange={(e) => handleUpdatePosition(idx, 'beschreibung', e.target.value)} multiline />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" variant="standard" type="number" value={pos.menge}
                          onChange={(e) => handleUpdatePosition(idx, 'menge', parseFloat(e.target.value) || 0)}
                          InputProps={{ inputProps: { step: 0.5, min: 0 } }} />
                      </TableCell>
                      <TableCell>
                        <Select size="small" variant="standard" value={pos.einheit}
                          onChange={(e) => handleUpdatePosition(idx, 'einheit', e.target.value)}>
                          <MenuItem value="PT">PT</MenuItem>
                          <MenuItem value="Stunden">Stunden</MenuItem>
                          <MenuItem value="Pauschal">Pauschal</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" variant="standard" type="number" value={pos.einzelpreis}
                          onChange={(e) => handleUpdatePosition(idx, 'einzelpreis', parseFloat(e.target.value) || 0)}
                          InputProps={{ inputProps: { step: 0.01 } }} />
                      </TableCell>
                      <TableCell align="right">{pos.gesamtpreis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleRemovePosition(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(formData.positionen || []).length === 0 && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography color="text.secondary">Keine Positionen</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Box sx={{ width: 300 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography>Nettobetrag:</Typography>
                  <Typography>{(formData.nettobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography>USt. {formData.steuersatz || 19}%:</Typography>
                  <Typography>{(formData.steuerbetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Bruttobetrag:</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{(formData.bruttobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/rechnungen')}>Zurück</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !formData.rechnungsnummer || !formData.projekt_id}>
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </Box>
      </Box>
    );
  }

  /* ══ Existing invoice view ══ */
  return (
    <Box>
      {/* ── Breadcrumbs + Actions ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <AppBreadcrumbs items={[{ label: 'Rechnungen', path: '/rechnungen' }, { label: rechnung?.rechnungsnummer || 'Rechnung' }]} />
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 2 }}>
          <Tooltip title="Änderungshistorie">
            <IconButton size="small" onClick={() => setHistorieOpen(true)}
              sx={{ color: 'text.secondary', '&:hover': { color: '#42a5f5', bgcolor: 'rgba(66,165,245,0.1)' } }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isEditable && (
            <Tooltip title="Löschen">
              <IconButton size="small" onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: 'text.secondary', '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="DOCX herunterladen">
            <IconButton size="small" onClick={() => rechnungenService.generateDocx(parseInt(id!))}
              sx={{ color: 'text.secondary', '&:hover': { color: '#42a5f5', bgcolor: 'rgba(66,165,245,0.1)' } }}>
              <DescriptionIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="PDF herunterladen">
            <IconButton size="small" onClick={() => rechnungenService.generatePdf(parseInt(id!))}
              sx={{ color: 'text.secondary', '&:hover': { color: '#e53935', bgcolor: 'rgba(229,57,53,0.1)' } }}>
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="E-Rechnung herunterladen (ZUGFeRD 2.x, PDF/A-3 mit eingebetteter XML)">
            <IconButton size="small" onClick={handleZugferdDownload} disabled={zugferdLoading}
              sx={{ color: 'text.secondary', '&:hover': { color: '#43a047', bgcolor: 'rgba(67,160,71,0.1)' } }}>
              {zugferdLoading ? <CircularProgress size={18} /> : <ReceiptLongIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {(rechnung?.zeiteintraege_ausserhalb_zeitraum || ausserhalbZeitraum.length) > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setSyncDialogOpen(true)} disabled={syncing}>
              Korrigieren
            </Button>
          }
        >
          {rechnung?.zeiteintraege_ausserhalb_zeitraum || ausserhalbZeitraum.length} Zeiteinträge
          ({(rechnung?.zeiteintraege_stunden_ausserhalb || ausserhalbZeitraum.reduce((s, z) => s + z.stunden, 0)).toFixed(1)}h)
          liegen außerhalb des Leistungszeitraums, sind aber als abgerechnet markiert.
          Die Rechnungsbeträge bleiben unverändert.
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'stretch' }}>

        {/* Left: Compact Infobox */}
        <Box
          sx={{
            flex: '0 0 calc(100% / 6 * 1.3)', minWidth: 182, maxWidth: 286,
            display: 'flex', flexDirection: 'column', gap: 0.75,
            px: 1.5, py: 1.5, borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            position: 'relative',
          }}
        >
          {isEditable && (
            editing ? (
              <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.25 }}>
                <Tooltip title="Speichern">
                  <IconButton size="small" onClick={handleSave} disabled={saving || !formData.rechnungsnummer}
                    sx={{ width: 22, height: 22, color: '#3dd47e', '&:hover': { bgcolor: 'rgba(61,212,126,0.15)' } }}>
                    {saving ? <CircularProgress size={12} color="inherit" /> : <CheckIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Abbrechen">
                  <IconButton size="small" onClick={handleCancelEditing}
                    sx={{ width: 22, height: 22, color: 'text.disabled', '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' } }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Tooltip title="Bearbeiten">
                <IconButton size="small" onClick={handleStartEditing}
                  sx={{ position: 'absolute', top: 6, right: 6, color: 'text.disabled', '&:hover': { color: '#3dd47e', bgcolor: 'rgba(61,212,126,0.1)' } }}>
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )
          )}

          {/* Rechnungsnummer */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Rechnungsnr.</FieldLabel>
            {editing ? (
              <TextField variant="standard" size="small" value={formData.rechnungsnummer}
                onChange={(e) => handleInputChange('rechnungsnummer', e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: '0.72rem', fontWeight: 600, p: 0, lineHeight: 1.2 } }} />
            ) : (
              <Typography sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.2 }}>{formData.rechnungsnummer}</Typography>
            )}
          </Box>

          {/* Projekt */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Projekt</FieldLabel>
            <Typography
              onClick={() => !editing && selectedProjekt && navigate(`/projekte/${selectedProjekt.id}`)}
              sx={{
                fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                ...(!editing && selectedProjekt && { cursor: 'pointer', '&:hover': { color: 'primary.main' } }),
              }}
            >
              {selectedProjekt?.name || '-'}
            </Typography>
          </Box>

          {/* Projektkunde */}
          {kundeName && (
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel>Projektkunde</FieldLabel>
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {kundeName}
              </Typography>
              {rechnung?.aufteilung_bezeichnung && (
                <Chip
                  size="small"
                  label={`${rechnung.aufteilung_bezeichnung}${rechnung.aufteilung_prozent != null ? ` ${rechnung.aufteilung_prozent} %` : ''}`}
                  sx={{ mt: 0.5, height: 18, fontSize: '0.6rem' }}
                />
              )}
            </Box>
          )}

          {/* Rechnungsempfänger */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Rechnungsempfänger</FieldLabel>
            {editing ? (
              <Select
                variant="standard"
                value={formData.rechnungsempfaenger_kunde_id ?? ''}
                onChange={(e) => handleInputChange('rechnungsempfaenger_kunde_id', e.target.value === '' ? null : Number(e.target.value))}
                disableUnderline
                sx={{ fontSize: '0.7rem', fontWeight: 500, maxWidth: '100%' }}
              >
                <MenuItem value="">Projektkunde</MenuItem>
                {kunden.map((k) => (
                  <MenuItem key={k.id} value={k.id}>{k.name_des_unternehmens}</MenuItem>
                ))}
              </Select>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {empfaengerName || '-'}
              </Typography>
            )}
          </Box>

          {/* Textvorlage */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Textvorlage</FieldLabel>
            {editing ? (
              <Select
                variant="standard"
                value={formData.textvorlage_id ?? ''}
                onChange={(e) => handleInputChange('textvorlage_id', e.target.value === '' ? null : Number(e.target.value))}
                disableUnderline
                sx={{ fontSize: '0.7rem', fontWeight: 500, maxWidth: '100%' }}
              >
                <MenuItem value="">Standard / Kundenzuordnung</MenuItem>
                {textvorlagen.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}{v.is_default ? ' (Standard)' : ''}</MenuItem>
                ))}
              </Select>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {textvorlagen.find((v) => v.id === formData.textvorlage_id)?.name || 'Standard'}
              </Typography>
            )}
          </Box>

          {/* Rechnungsdatum */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Rechnungsdatum</FieldLabel>
            {editing ? (
              <TextField variant="standard" size="small" type="date" value={formData.rechnungsdatum}
                onChange={(e) => handleInputChange('rechnungsdatum', e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }} />
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>{formatDate(formData.rechnungsdatum)}</Typography>
            )}
          </Box>

          {/* Leistungszeitraum */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Leistungszeitraum</FieldLabel>
            {editing ? (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField variant="standard" size="small" type="date" value={formData.leistungszeitraum_von}
                  onChange={(e) => handleInputChange('leistungszeitraum_von', e.target.value)}
                  InputProps={{ disableUnderline: true }}
                  sx={{ '& input': { fontSize: '0.65rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>–</Typography>
                <TextField variant="standard" size="small" type="date" value={formData.leistungszeitraum_bis}
                  onChange={(e) => handleInputChange('leistungszeitraum_bis', e.target.value)}
                  InputProps={{ disableUnderline: true }}
                  sx={{ '& input': { fontSize: '0.65rem', fontWeight: 500, p: 0, lineHeight: 1.2 }, flex: 1 }} />
              </Box>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                {formatDate(formData.leistungszeitraum_von)} – {formatDate(formData.leistungszeitraum_bis)}
              </Typography>
            )}
          </Box>

          {/* Zahlungsziel */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Zahlungsziel</FieldLabel>
            {editing ? (
              <TextField variant="standard" size="small" type="number" value={formData.zahlungsziel_tage}
                onChange={(e) => handleInputChange('zahlungsziel_tage', parseInt(e.target.value) || 30)}
                InputProps={{ disableUnderline: true }}
                sx={{ '& input': { fontSize: '0.7rem', fontWeight: 500, p: 0, lineHeight: 1.2 } }} />
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>{formData.zahlungsziel_tage} Tage</Typography>
            )}
          </Box>

          {/* Steuerfall */}
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Steuerfall</FieldLabel>
            {editing ? (
              <Select
                variant="standard"
                value={formData.steuerfall || 'inland'}
                onChange={(e) => handleInputChange('steuerfall', e.target.value)}
                disableUnderline
                sx={{ fontSize: '0.7rem', fontWeight: 500, maxWidth: '100%' }}
              >
                {STEUERFALL_OPTIONEN.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            ) : (
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', lineHeight: 1.2 }}>
                {STEUERFALL_OPTIONEN.find((o) => o.value === formData.steuerfall)?.label || `${formData.steuersatz}%`}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: Status Pipeline + KPIs */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <InvoiceStatusPipeline
            currentStatus={rechnung?.status || 'Entwurf'}
            fillHeight
            {...(isEditable && { onStatusChange: handleStatusChange })}
          />

          {/* KPI Cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{
              flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                Netto
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {(formData.nettobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </Box>
            <Box sx={{
              flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
              bgcolor: isDark ? 'rgba(255,152,0,0.08)' : 'rgba(255,152,0,0.06)',
              border: `1px solid ${isDark ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.12)'}`,
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                USt. {formData.steuersatz || 19}%
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#ff9800' }}>
                {(formData.steuerbetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </Box>
            <Box sx={{
              flex: '1 1 100px', px: 2, py: 1.5, borderRadius: 1.5, textAlign: 'center',
              bgcolor: isDark ? 'rgba(76,175,80,0.08)' : 'rgba(76,175,80,0.06)',
              border: `1px solid ${isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.12)'}`,
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', display: 'block' }}>
                Brutto
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#4caf50' }}>
                {(formData.bruttobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {(steuerfallHinweis(formData.steuerfall) || rechnung?.steuerfall_hinweis) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {steuerfallHinweis(formData.steuerfall) || rechnung?.steuerfall_hinweis}
        </Alert>
      )}

      {/* ── Inline edit menus ── */}
      <Menu anchorEl={projektMenuAnchor} open={Boolean(projektMenuAnchor)} onClose={() => setProjektMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 280 } } }}>
        {projekte.map((p) => (
          <MenuItem key={p.id} selected={p.id === formData.projekt_id}
            onClick={() => { handleInputChange('projekt_id', p.id); setProjektMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
            {p.name}
          </MenuItem>
        ))}
      </Menu>

      {rechnung?.aufteilung_gruppe_id && rechnung.partner_rechnung_id && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => navigate(`/rechnungen/${rechnung.partner_rechnung_id}`)}>
              Partnerrechnung
            </Button>
          }
        >
          Aufgeteilt als {rechnung.aufteilung_bezeichnung || 'Anteil'}
          {rechnung.aufteilung_prozent != null ? ` (${rechnung.aufteilung_prozent} %)` : ''}.
          Partner: {rechnung.partner_rechnungsnummer || `#${rechnung.partner_rechnung_id}`}.
        </Alert>
      )}

      {rechnung?.aufteilung_verfuegbar && isEditable && splitAnteile.length === 2 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CallSplitIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aufteilen für zwei Empfänger</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Erzeugt zwei eigenständige Rechnungen mit eigenen Nummern, PDFs und E-Rechnungen.
            </Typography>
            <Grid container spacing={2}>
              {splitAnteile.map((anteil, idx) => (
                <Grid item xs={12} sm={6} key={`${anteil.kunde_id}-${idx}`}>
                  <TextField
                    fullWidth
                    label={`${anteil.bezeichnung} (%)`}
                    type="number"
                    value={anteil.prozent}
                    onChange={(e) => handleSplitProzent(idx, parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<CallSplitIcon />}
                onClick={() => setSplitDialogOpen(true)}
                disabled={splitting}
              >
                In zwei Rechnungen aufteilen
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ══ Accordion: Positionen ══ */}
      <Accordion expanded={expandedPanels.positionen} onChange={handleAccordionChange('positionen')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <ShoppingCartOutlinedIcon sx={{ fontSize: 20, color: '#42a5f5' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Positionen</Typography>
          {(formData.positionen || []).length > 0 && (
            <Chip label={`${(formData.positionen || []).length}`} size="small"
              sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: isDark ? 'rgba(66,165,245,0.15)' : 'rgba(66,165,245,0.1)', color: '#42a5f5' }} />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          {isEditable && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
              {isKontingentProjekt && !isNew && (
                <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={handleGenerateFromZeiteintraege}>
                  Aus Zeiteinträgen generieren
                </Button>
              )}
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddPosition}>
                Position hinzufügen
              </Button>
            </Box>
          )}

          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40}>Pos.</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell width={100}>Menge</TableCell>
                  <TableCell width={100}>Einheit</TableCell>
                  <TableCell width={130}>Einzelpreis</TableCell>
                  <TableCell width={130} align="right">Gesamt</TableCell>
                  {isEditable && <TableCell width={50} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {(formData.positionen || []).map((pos, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      {isEditable ? (
                        <TextField fullWidth size="small" variant="standard" value={pos.beschreibung}
                          onChange={(e) => handleUpdatePosition(idx, 'beschreibung', e.target.value)} multiline />
                      ) : pos.beschreibung}
                    </TableCell>
                    <TableCell>
                      {isEditable ? (
                        <TextField size="small" variant="standard" type="number" value={pos.menge}
                          onChange={(e) => handleUpdatePosition(idx, 'menge', parseFloat(e.target.value) || 0)}
                          InputProps={{ inputProps: { step: 0.5, min: 0 } }} />
                      ) : pos.menge.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {isEditable ? (
                        <Select size="small" variant="standard" value={pos.einheit}
                          onChange={(e) => handleUpdatePosition(idx, 'einheit', e.target.value)}>
                          <MenuItem value="PT">PT</MenuItem>
                          <MenuItem value="Stunden">Stunden</MenuItem>
                          <MenuItem value="Pauschal">Pauschal</MenuItem>
                        </Select>
                      ) : pos.einheit}
                    </TableCell>
                    <TableCell>
                      {isEditable ? (
                        <TextField size="small" variant="standard" type="number" value={pos.einzelpreis}
                          onChange={(e) => handleUpdatePosition(idx, 'einzelpreis', parseFloat(e.target.value) || 0)}
                          InputProps={{ inputProps: { step: 0.01 } }} />
                      ) : `${pos.einzelpreis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`}
                    </TableCell>
                    <TableCell align="right">{pos.gesamtpreis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</TableCell>
                    {isEditable && (
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleRemovePosition(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(formData.positionen || []).length === 0 && (
                  <TableRow><TableCell colSpan={isEditable ? 7 : 6} align="center" sx={{ py: 3 }}><Typography color="text.secondary">Keine Positionen</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Box sx={{ width: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">Nettobetrag:</Typography>
                <Typography variant="body2">{(formData.nettobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">USt. {formData.steuersatz || 19}%:</Typography>
                <Typography variant="body2">{(formData.steuerbetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="bold">Bruttobetrag:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{(formData.bruttobetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</Typography>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ══ Accordion: Verknüpfte Zeiteinträge ══ */}
      <Accordion expanded={expandedPanels.zeiteintraege} onChange={handleAccordionChange('zeiteintraege')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <AccessTimeIcon sx={{ fontSize: 20, color: '#ff9800' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Verknüpfte Zeiteinträge</Typography>
          {linkedEntries.length > 0 && (
            <Chip
              label={`${linkedEntries.length}`}
              size="small"
              sx={{ ml: 1, fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: isDark ? 'rgba(255,152,0,0.15)' : 'rgba(255,152,0,0.1)', color: '#ff9800' }}
            />
          )}
          {ausserhalbZeitraum.length > 0 && (
            <Chip label={`${ausserhalbZeitraum.length} außerhalb`} size="small" color="warning" sx={{ ml: 1, height: 22, fontSize: '0.7rem' }} />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          {ausserhalbZeitraum.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button size="small" variant="outlined" color="warning" startIcon={<LinkOffIcon />} onClick={() => setSyncDialogOpen(true)}>
                Einträge außerhalb des Zeitraums lösen
              </Button>
            </Box>
          )}
          {linkedEntries.length === 0 ? (
            <Alert severity="info">Keine Zeiteinträge mit dieser Rechnung verknüpft.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Datum</TableCell>
                    <TableCell align="right">Stunden</TableCell>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Zeitraum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedEntries.map((ze) => {
                    const ausserhalb = ze.datum < formData.leistungszeitraum_von || ze.datum > formData.leistungszeitraum_bis;
                    return (
                      <TableRow key={ze.id} sx={ausserhalb ? { bgcolor: 'rgba(255,152,0,0.08)' } : undefined}>
                        <TableCell>{formatDate(ze.datum)}</TableCell>
                        <TableCell align="right">{ze.stunden.toFixed(1)}h</TableCell>
                        <TableCell>{ze.beschreibung || '-'}</TableCell>
                        <TableCell>
                          {ausserhalb ? (
                            <Chip label="Außerhalb" size="small" color="warning" />
                          ) : (
                            <Chip label="Im Zeitraum" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ══ Accordion: Auftragsreferenzen ══ */}
      <Accordion expanded={expandedPanels.referenzen} onChange={handleAccordionChange('referenzen')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <AccountBalanceOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Auftragsreferenzen</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          <Grid container spacing={2}>
            {referenzFelder(isEditable)}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ══ Accordion: Zahlungsbedingungen / Notizen ══ */}
      <Accordion expanded={expandedPanels.notizen} onChange={handleAccordionChange('notizen')} sx={accordionSx(isDark)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
          <NotesOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zahlungsbedingungen & Notizen</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            {rechnung?.zahlungsfrist_hinweis && (
              <Grid item xs={12}>
                <Alert severity="warning">{rechnung.zahlungsfrist_hinweis}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Zahlungsbedingungen</Typography>
              {isEditable ? (
                <TextField fullWidth size="small" value={formData.zahlungsbedingungen}
                  onChange={(e) => handleInputChange('zahlungsbedingungen', e.target.value)} multiline rows={2} />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{formData.zahlungsbedingungen || '-'}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Notizen (intern)</Typography>
              {isEditable ? (
                <TextField fullWidth size="small" value={formData.notizen}
                  onChange={(e) => handleInputChange('notizen', e.target.value)} multiline rows={2} />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: formData.notizen ? 'normal' : 'italic', color: formData.notizen ? 'text.primary' : 'text.disabled' }}>
                  {formData.notizen || 'Keine Notizen'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ── Dialogs ── */}
      <ConfirmDialog
        open={deleteDialogOpen} title="Rechnung löschen"
        message="Möchten Sie diese Rechnung wirklich löschen?"
        onConfirm={handleDelete} onCancel={() => setDeleteDialogOpen(false)}
      />
      <ConfirmDialog
        open={splitDialogOpen} title="Rechnung aufteilen"
        message="Die Entwurfsrechnung wird in zwei Rechnungen mit den angegebenen Anteilen umgewandelt. Fortfahren?"
        onConfirm={handleAufteilen} onCancel={() => setSplitDialogOpen(false)}
        confirmText="Aufteilen"
      />
      <ConfirmDialog
        open={syncDialogOpen}
        title="Zeiteinträge außerhalb des Zeitraums lösen"
        message={`${ausserhalbZeitraum.length} Zeiteinträge (${ausserhalbZeitraum.reduce((s, z) => s + z.stunden, 0).toFixed(1)}h) liegen außerhalb des Leistungszeitraums und werden wieder auf „nicht abgerechnet“ gesetzt. Die Rechnungspositionen und Beträge bleiben unverändert.`}
        onConfirm={handleSyncZeiteintraege}
        onCancel={() => setSyncDialogOpen(false)}
        confirmText="Korrigieren"
      />
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Aus Zeiteinträgen generieren</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Es werden nur nicht abgerechnete Zeiteinträge im Leistungszeitraum{' '}
            <strong>{formatDate(formData.leistungszeitraum_von)} – {formatDate(formData.leistungszeitraum_bis)}</strong>{' '}
            als abgerechnet markiert.
          </Typography>
          {generatePreview && (
            <Typography variant="body2" color="text.secondary">
              {generatePreview.anzahl} Einträge, {generatePreview.stunden.toFixed(1)}h
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Einträge vor oder nach diesem Zeitraum bleiben unberührt.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGenerateDialogOpen(false)} sx={{ color: 'text.secondary' }}>Abbrechen</Button>
          <Button variant="contained" onClick={handleConfirmGenerate} disabled={generating}>
            {generating ? 'Generiere…' : 'Generieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {id && (
        <HistorieTimeline
          entitaetTyp="Rechnung" entitaetId={parseInt(id)}
          open={historieOpen} onClose={() => setHistorieOpen(false)}
        />
      )}
    </Box>
  );
};

export default RechnungDetailPage;
